import { motion } from "framer-motion";
import { Youtube, Send, TrendingUp, LineChart, ArrowUpRight } from "lucide-react";
import { BRAND } from "@/constants/brand";
import { useTheme } from "@/contexts/ThemeContext";
import MarketAmbient from "@/components/MarketAmbient";

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const item = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const Hero = () => {
  const { theme } = useTheme();
  const heroLogo = theme === "dark" ? BRAND.logoDark : BRAND.logoLight;

  return (
    <section id="home" className="relative overflow-hidden pt-32 pb-16 md:pt-40 md:pb-24">
      {/* Backdrop */}
      <div className="pointer-events-none absolute inset-0 grid-bg" aria-hidden="true" />
      <MarketAmbient opacity={theme === "dark" ? 0.09 : 0.07} />
      <div
        className="aurora"
        style={{
          top: "-6rem",
          left: "-6rem",
          width: "28rem",
          height: "28rem",
          background: "radial-gradient(circle at center, hsl(var(--brand-orange)) 0%, transparent 60%)",
        }}
      />
      <div
        className="aurora"
        style={{
          bottom: "-8rem",
          right: "-6rem",
          width: "32rem",
          height: "32rem",
          background: "radial-gradient(circle at center, hsl(var(--brand-navy)) 0%, transparent 60%)",
        }}
      />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 md:px-10 lg:grid-cols-12 lg:gap-8">
        {/* Left: Text */}
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="lg:col-span-7"
        >
          <motion.span variants={item} className="chip" data-testid="hero-badge">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--brand-orange))]" />
            {BRAND.tagline}
          </motion.span>

          <motion.h1
            variants={item}
            className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl xl:text-7xl"
            data-testid="hero-heading"
          >
            Your Trusted Partner in{" "}
            <span className="text-gradient-brand">Stock Market</span>
            <br />
            <span className="text-foreground">Investing &amp; </span>
            <span className="relative inline-block">
              <span className="relative z-10">Wealth Creation</span>
              <svg
                aria-hidden="true"
                className="absolute -bottom-2 left-0 z-0 h-3 w-full"
                viewBox="0 0 300 12"
                preserveAspectRatio="none"
              >
                <path
                  d="M2 8 Q75 2 150 6 T298 4"
                  fill="none"
                  stroke="hsl(var(--brand-orange))"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </motion.h1>

          <motion.p
            variants={item}
            className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg"
            data-testid="hero-subtitle"
          >
            Welcome to <span className="font-semibold text-foreground">Buggu Capital</span> — where we simplify the world of finance and help you make smarter investment decisions. Practical, hype-free strategies for beginners and experienced investors alike.
          </motion.p>

          <motion.div variants={item} className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href={BRAND.socials.telegram}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-brand"
              data-testid="hero-cta-telegram"
            >
              <Send size={18} /> Join Telegram
            </a>
            <a
              href={BRAND.socials.youtube}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline-brand"
              data-testid="hero-cta-youtube"
            >
              <Youtube size={18} /> Watch on YouTube
            </a>
          </motion.div>

          <motion.div
            variants={item}
            className="mt-10 flex flex-wrap items-center gap-6 text-xs uppercase tracking-widest text-muted-foreground"
          >
            <div className="flex items-center gap-2">
              <TrendingUp size={14} className="text-[hsl(var(--brand-green))]" />
              <span>Nifty • Bank Nifty</span>
            </div>
            <div className="flex items-center gap-2">
              <LineChart size={14} className="text-[hsl(var(--brand-orange))]" />
              <span>Options • Swing • Intraday</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[hsl(var(--brand-green))]">₿</span>
              <span>Crypto Insights</span>
            </div>
          </motion.div>
        </motion.div>

        {/* Right: Visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
          className="relative lg:col-span-5"
        >
          <div className="relative mx-auto aspect-square w-full max-w-md">
            {/* Big glow */}
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background:
                  "radial-gradient(circle at 50% 50%, hsl(var(--brand-orange)/0.35) 0%, transparent 60%)",
                filter: "blur(40px)",
              }}
              aria-hidden="true"
            />
            {/* Logo tile - transparent to let theme-native logo shine */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-6 flex items-center justify-center rounded-3xl p-6"
            >
              <img
                key={theme}
                src={heroLogo}
                alt="Buggu Capital Logo"
                loading="eager"
                decoding="async"
                className="h-full w-full object-contain drop-shadow-2xl"
                data-testid="hero-logo-image"
              />
            </motion.div>

            {/* Floating cards */}
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
              className="glass absolute -left-4 top-8 rounded-2xl p-4 shadow-lg"
              data-testid="hero-float-card-nifty"
            >
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">NIFTY 50</p>
              <p className="mt-1 font-display text-lg font-bold text-[hsl(var(--brand-green))]">+1.24%</p>
              <div className="mt-1 flex h-6 items-end gap-0.5">
                {[3, 5, 4, 7, 6, 9, 8, 11].map((h, i) => (
                  <span
                    key={i}
                    className="w-1.5 rounded-sm bg-[hsl(var(--brand-green))]"
                    style={{ height: `${h * 3}px` }}
                  />
                ))}
              </div>
            </motion.div>

            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="glass absolute -right-4 bottom-16 rounded-2xl p-4 shadow-lg"
              data-testid="hero-float-card-btc"
            >
              <div className="flex items-center gap-2">
                <span className="text-lg text-[hsl(var(--brand-orange))]">₿</span>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Bitcoin</p>
              </div>
              <p className="mt-1 font-display text-lg font-bold">$67.4K</p>
              <p className="text-[10px] text-[hsl(var(--brand-green))]">▲ 2.8% today</p>
            </motion.div>

            <motion.div
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
              className="glass absolute -bottom-2 left-8 rounded-2xl p-3 shadow-lg"
              data-testid="hero-float-card-signal"
            >
              <div className="flex items-center gap-2">
                <div className="rounded-full bg-[hsl(var(--brand-green)/0.15)] p-1.5">
                  <ArrowUpRight size={14} className="text-[hsl(var(--brand-green))]" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Bank Nifty</p>
                  <p className="text-xs font-semibold">Bullish Setup</p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
